import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CartMerchantAmountBlockConfig } from '@front-platform/content-builder';
import { cartMerchantAmountBlockResolveConfig } from '@front-platform/content-builder';
import { CommonModule } from '@angular/common';
import { Component, inject, input, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import {
  DoodCreateOrderQuery,
  DoodOrderType,
  DoodValidateOrderQuery,
  IOrderItem,
} from '@store/order/order.model';

import { UserKeys } from '@config/keys/user.keys';
import { CartKeys } from '@config/keys/cart.keys';
import { OrderKeys } from '@config/keys/order.keys';
import { DistributionModeKeys, ShopKeys } from '@config/keys/shop.keys';

import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { BasketStoreSelector } from '@common/selectors/basket.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';

import { Paths } from '@config/paths.config';
import { GroupedOrderModalsAttributes } from '@config/modals/grouped-order-modals.config';
import { ORDER_ALREADY_CREATED_WITH_CART, USER_IS_ALREADY_CONFIRMED } from '@config/errors.config';

import { DistributionModeValues, OnSiteLocationPickModeValues } from '@config/values/order.values';

import { CartService } from '@core/services/cart/cart.service';
import { UserService } from '@core/services/user/user.service';
import { BasketService } from '@core/services/basket/basket.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { OrdersService } from '@core/services/orders/orders.service';
import { ErrorService } from '@core/services/error-service/error.service';
import { OrderTypeService } from '@core/services/order-type/order-type.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';

import { CartParametersModalComponent } from '@shared/modals/cart-parameters-modal/cart-parameters-modal.component';
import { ShareGroupedOrderModalComponent } from '@shared/modals/share-grouped-order-modal/share-grouped-order-modal.component';
import { CartUserInformationsModalComponent } from '@shared/modals/cart-user-informations-modal/cart-user-informations-modal.component';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { UntypedModalIO } from '@core/services/modals/modals-util.type';
import { NO_PREPAYMENT_COUPON } from '@core/models/cart.model';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';
import { CartTotalAmountAtomComponent } from '@shared/atoms/cart-total-amount-atom/cart-total-amount-atom.component';
import { OverviewCartAdditionalAtomComponent } from '@shared/atoms/overview-cart-additional-atom/overview-cart-additional-atom.component';
import { provideCartMerchantAmountBlockDataSource } from './cart-merchant-amount-block-data-source.providers';
import { CART_MERCHANT_AMOUNT_BLOCK_DATA_SOURCE } from './cart-merchant-amount-block-view-model';
export interface CartMerchantAmountBlockRuntimeConfig {}

export type CartMerchantAmountBlockConfigInput = Partial<
  Omit<CartMerchantAmountBlockConfig, keyof CartMerchantAmountBlockRuntimeConfig> &
    CartMerchantAmountBlockRuntimeConfig
>;
export type CartMerchantAmountBlockResolvedConfig = Omit<
  CartMerchantAmountBlockConfig,
  keyof CartMerchantAmountBlockRuntimeConfig
> &
  Partial<CartMerchantAmountBlockRuntimeConfig>;
export interface CartMerchantAmountBlockContract extends ConfigComponentContract<
  CartMerchantAmountBlockConfigInput,
  CartMerchantAmountBlockResolvedConfig
> {}

@Component({
  selector: 'app-cart-merchant-amount-block',
  templateUrl: './cart-merchant-amount-block.component.html',
  standalone: true,
  providers: [...provideCartMerchantAmountBlockDataSource()],
  imports: [
    CommonModule,
    ContentBuilderColorPipe,
    TranslateModule,
    OverviewCartAdditionalAtomComponent,
    CartTotalAmountAtomComponent,
    ButtonAtomComponent,
  ],
})
export class CartMerchantAmountBlockComponent implements CartMerchantAmountBlockContract {
  readonly config = input<CartMerchantAmountBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CartMerchantAmountBlockConfig,
    CartMerchantAmountBlockRuntimeConfig
  >(cartMerchantAmountBlockResolveConfig, () => ({ config: this.config() }));
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly cartSelector = inject(CartStoreSelector);
  private readonly shopSelector = inject(ShopStoreSelector);
  private readonly authSelector = inject(AuthStoreSelector);
  private readonly orderSelector = inject(OrderStoreSelector);
  private readonly basketSelector = inject(BasketStoreSelector);
  private readonly cartService = inject(CartService);
  private readonly errorService = inject(ErrorService);
  private readonly orderDispatcher = inject(OrderStoreDispatcher);
  private readonly ordersService = inject(OrdersService);
  private readonly modalsService = inject(ModalsService);
  private readonly basketService = inject(BasketService);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly translateService = inject(TranslateService);
  private readonly orderTypeService = inject(OrderTypeService);

  readonly headingColor = computed(
    () => this.resolvedConfig()['heading_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly textColor = computed(
    () => this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly secondaryTextColor = computed(
    () => this.resolvedConfig()['secondary_text_color'] as unknown as IContentBuilderFieldColor,
  );

  missingPhone$ = this.orderSelector.selectErrors.pipe(map(errors => errors.missing_number));

  readonly merchantAmountVm$ = inject(CART_MERCHANT_AMOUNT_BLOCK_DATA_SOURCE).vm$;

  basket$ = this.basketSelector.select;
  userId$ = this.authSelector.selectUserId;
  basketId$ = this.basketSelector.select.pipe(map(basket => basket.id));
  orderKeys = OrderKeys;
  missingLocationIdError = '';
  errorMessages: string[] = [];
  isGoToPaymentInProgress = false;
  onSiteLocationIsMissing = false;

  constructor() {
    this.errorService
      .getErrorMessage$()
      .pipe(
        takeUntilDestroyed(),
        tap(messages => (this.errorMessages = messages)),
      )
      .subscribe();

    this.cartSelector.selectActive$
      .pipe(
        takeUntilDestroyed(),
        map(cart => cart?.on_site_location_id),
        tap(onSiteLocation => {
          if (onSiteLocation) {
            this.checkIfOnSiteLocationIsMissing();
          }
        }),
      )
      .subscribe();
  }

  removeCoupon($event: any): void {
    if ($event.type === 'PREPAYMENT') {
      this.cartService.addCoupon(NO_PREPAYMENT_COUPON);
      this.orderDispatcher.checkCartIsValid();
      return;
    }
    this.cartService.removeCoupon($event);
    this.orderDispatcher.checkCartIsValid();
  }

  removeUsePoints(): void {
    this.cartService.setUsePoints(undefined);
    this.orderDispatcher.checkCartIsValid();
  }

  goToPayment(): void {
    this.isGoToPaymentInProgress = true;
    this.orderDispatcher
      .checkCartIsValid$()
      .pipe(
        take(1),
        map(order => {
          if (
            order &&
            order.type &&
            order.contact_phone_number === null &&
            order.type === DoodOrderType.delivery
          ) {
            this.ordersService.setDefaultPhone();
            this.modalsService.open<UntypedModalIO>(CartUserInformationsModalComponent.handle);
            return false;
          }
          return true;
        }),
      )
      .subscribe({
        next: value => {
          if (value) {
            this.router.navigate([
              this.routerHelper.translateRoute(`/${Paths.Cart}/${Paths.Funnel}`),
            ]);
          }
        },
        complete: () => {
          this.isGoToPaymentInProgress = false;
        },
      });
  }

  confirmOrder(): void {
    this.isGoToPaymentInProgress = true;
    this.basketService
      .getBasket$(this.basketSelector.basket.id, this.basketSelector.basket.share_code)
      .pipe(
        switchMap(() => this.basketService.confirmBasket$()),
        take(1),
        catchError(err => {
          this.isGoToPaymentInProgress = false;
          if (
            err?.error?.detail === USER_IS_ALREADY_CONFIRMED ||
            err?.error?.detail === ORDER_ALREADY_CREATED_WITH_CART
          ) {
            return of([]);
          }

          if (err.status === 403) {
            alert(this.translateService.instant('group-order-modal.order-cancelled'));
            this.ordersService.clearOrders();
            this.basketService.clearBasket();
            this.cartService.clearCart();
            this.router.navigate([this.routerHelper.translateRoute('/')]);
          }

          return throwError(() => err);
        }),
      )
      .subscribe(() => {
        this.isGoToPaymentInProgress = false;
        this.router.navigate([
          this.routerHelper.translateRoute(`/${Paths.Validate}/${Paths.SharedOrder}`),
        ]);
      });
  }

  exitGroupOrder(): void {
    this.modalsService.open<UntypedModalIO>(
      ShareGroupedOrderModalComponent.handle,
      GroupedOrderModalsAttributes.LeavingOrder,
    );
  }

  cancelGroupOrder(): void {
    this.modalsService.open<UntypedModalIO>(
      ShareGroupedOrderModalComponent.handle,
      GroupedOrderModalsAttributes.CancelOrder,
    );
  }

  getServiceCharges(
    orderValidate: DoodValidateOrderQuery | DoodCreateOrderQuery | null | undefined,
  ): IOrderItem[] {
    const products = orderValidate?.products ?? [];
    return products.filter(product => product.is_service_charge) as IOrderItem[];
  }

  private checkIfOnSiteLocationIsMissing(): boolean {
    const shop = this.shopSelector.active;
    const cart = this.cartSelector.active;
    const basket = this.basketSelector.basket;
    const user = this.userService.user;
    const distributionMode = cart?.[CartKeys.Type];
    const orderTypeCapabilities = this.orderTypeService.getCapabilities(distributionMode);

    const onsiteLocationPickMode = shop?.[ShopKeys.DistributionModes].filter(v => {
      return v[DistributionModeKeys.Type] === DistributionModeValues.OnSite;
    })?.[0]?.[DistributionModeKeys.OnsiteLocationPickMode];

    if (cart && orderTypeCapabilities.isOnSiteLocationMandatory) {
      this.onSiteLocationIsMissing = !!!cart[CartKeys.OnSiteLocationName];

      if (
        this.onSiteLocationIsMissing &&
        onsiteLocationPickMode === OnSiteLocationPickModeValues.Url &&
        cart?.[CartKeys.Type] === DistributionModeValues.OnSite
      ) {
        this.missingLocationIdError = this.translateService.instant('basket.please-scan-qr-code');
        return false;
      }

      if (shop && !!!cart[CartKeys.OnSiteLocationName] && !basket?.[CartKeys.Id]) {
        this.modalsService.open<UntypedModalIO>(CartParametersModalComponent.handle);
      }

      if (
        shop &&
        !!!cart[CartKeys.OnSiteLocationName] &&
        basket?.[CartKeys.User]?.[UserKeys.Id] === user?.[UserKeys.Id]
      ) {
        this.modalsService.open<UntypedModalIO>(CartParametersModalComponent.handle);
      }
      return !!cart[CartKeys.OnSiteLocationName];
    }

    return false;
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CartMerchantAmountBlockComponent, {
  'keyAliases': {},
});
